/**
 * ProposalModal.js
 * "Wisdom of Stars" submission modal.
 */
import { ProposalService } from '../../services/proposal.service.js';
import { APP_VERSION } from '../../version.js';
import { locale } from '../../services/LocaleService.js';

export class ProposalModal {
    constructor() {
        this.render();
    }

    render() {
        if (document.getElementById('proposal-modal-overlay')) return;

        // 1. Overlay (Background)
        const overlay = document.createElement('div');
        overlay.id = 'proposal-modal-overlay';
        overlay.className = "fixed inset-0 bg-black/50 z-[9990] transition-opacity opacity-0 backdrop-blur-sm";

        // 2. Wrapper (Positioning)
        const wrapper = document.createElement('div');
        wrapper.className = "fixed inset-0 z-[10001] flex items-center justify-center pointer-events-none p-4";

        // 3. Modal Box (Interactive)
        const modal = document.createElement('div');
        modal.className = "bg-white dark:bg-zinc-900 w-full max-w-lg rounded-2xl shadow-2xl transform scale-95 opacity-0 transition-all duration-300 relative pointer-events-auto border border-gray-100 dark:border-zinc-800 flex flex-col max-h-[90vh]";

        // Header
        const header = document.createElement('div');
        header.className = "p-5 border-b border-gray-100 dark:border-zinc-800 flex justify-between items-center";
        header.innerHTML = `
            <div>
                <h3 class="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                    <span class="material-symbols-outlined text-yellow-500">lightbulb</span>
                    ${locale.t('proposal.title')}
                </h3>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">${locale.t('proposal.desc')}</p>
            </div>
            <button class="btn-close p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full transition-colors">
                <span class="material-symbols-outlined">close</span>
            </button>
        `;

        // Content (Form)
        const content = document.createElement('div');
        content.className = "p-6 overflow-y-auto no-scrollbar";
        content.innerHTML = `
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">${locale.t('proposal.label_title')}</label>
                    <input type="text" id="prop-title" 
                        class="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all placeholder-gray-400 text-sm"
                        placeholder="${locale.t('proposal.placeholder_title')}">
                </div>
                
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">${locale.t('proposal.label_content')}</label>
                    <textarea id="prop-content" rows="6"
                        class="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all placeholder-gray-400 text-sm resize-none"
                        placeholder="${locale.t('proposal.placeholder_content')}"
                    ></textarea>
                </div>
            </div>

            <div class="mt-8 flex justify-end">
                <button id="btn-submit-prop" class="flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-yellow-500/30 disabled:opacity-50 disabled:cursor-not-allowed">
                    <span class="material-symbols-outlined text-sm">send</span>
                    ${locale.t('proposal.btn_submit')}
                </button>
            </div>
        `;

        modal.appendChild(header);
        modal.appendChild(content);
        wrapper.appendChild(modal);

        document.body.appendChild(overlay);
        document.body.appendChild(wrapper);

        this.overlay = overlay;
        this.wrapper = wrapper;
        this.modal = modal;

        // Animations
        requestAnimationFrame(() => {
            overlay.classList.remove('opacity-0');
            modal.classList.remove('scale-95', 'opacity-0');
            modal.classList.add('scale-100', 'opacity-100');
        });

        // Event Binding
        this.bindEvents();
    }

    bindEvents() {
        const closeBtn = this.modal.querySelector('.btn-close');

        // Close Actions
        const close = () => {
            this.overlay.classList.add('opacity-0');
            this.modal.classList.remove('scale-100', 'opacity-100');
            this.modal.classList.add('scale-95', 'opacity-0');
            setTimeout(() => {
                if (this.overlay.isConnected) this.overlay.remove();
                if (this.wrapper.isConnected) this.wrapper.remove();
            }, 300);
        };

        closeBtn.onclick = close;

        // Simple Overlay Click
        this.overlay.onclick = () => close();

        // Submit Action
        const submitBtn = this.modal.querySelector('#btn-submit-prop');
        const titleInput = this.modal.querySelector('#prop-title');
        const contentInput = this.modal.querySelector('#prop-content');

        submitBtn.onclick = async () => {
            if (!window.user) {
                // [FIX] Explicitly close self before showing AuthRequireModal
                close();

                // [FIX] Use AuthRequireModal via Manager
                const { AuthRequireModal } = await import('./AuthRequireModal.js');
                const { ModalManager } = await import('../../utils/ModalManager.js');
                ModalManager.show(AuthRequireModal);
                return;
            }

            const title = titleInput.value.trim();
            const content = contentInput.value.trim();

            if (!title || !content) {
                window.Modal.alert(locale.t('common.error')); // Fallback or add specific error key
                return;
            }

            submitBtn.disabled = true;
            submitBtn.innerHTML = `<div class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div> ${locale.t('common.loading')}`;

            try {
                await ProposalService.submitProposal({
                    title,
                    content,
                    version: APP_VERSION
                });

                close(); // Close Modal first

                // [FIX] Use SweetAlert2 for Centered & Personalized Feedback
                const userName = window.user?.displayName || 'User';
                if (typeof Swal !== 'undefined') {
                    Swal.fire({
                        title: locale.t('proposal.success_title'),
                        html: `<div class="text-gray-600 font-medium py-2">${locale.t('proposal.success_desc', { name: userName })}</div>`,
                        icon: 'success',
                        confirmButtonColor: '#fbbf24', // Amber/Yellow
                        confirmButtonText: locale.t('common.confirm')
                    });
                } else {
                    window.Modal.alert(locale.t('proposal.success_desc', { name: userName }));
                }

            } catch (error) {
                console.error(error);
                window.Modal.alert(error.message || locale.t('common.error'));
                submitBtn.disabled = false;
                submitBtn.innerHTML = `<span class="material-symbols-outlined text-sm">send</span> ${locale.t('proposal.btn_submit')}`;
            }
        };
    }
}
